import React, {
  createContext,
  PropsWithChildren,
  useMemo,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import values from 'lodash/values';
import {
  QueryObserverResult,
  useQuery,
} from '@tanstack/react-query';
import {
  PagePropertiesDTO,
} from 'dtos';
import {
  Pagination,
} from 'pages/Dashboard/types/common';
import compact from 'lodash/compact';

export type ContextProps = {
  data: any[];
  auxiliary?: unknown;
  refetch: ((...params: any[]) => Promise<QueryObserverResult<any, unknown>>) | null;
  error: unknown;
  isLoading: boolean;
  isFetching: boolean;
  isFetched: boolean;
  isError: boolean;
  isEmpty: boolean;
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  totalSize: number;
  setTotalSize: Dispatch<SetStateAction<number>>;
};
export const DataContext = createContext<Partial<ContextProps>>({});

type Props<K, T, A = unknown> = PropsWithChildren & {
  normalize?: (raw: K) => T[] | undefined;
  getData: (...params: any[]) => Promise<K> | K;
  getQuery: (...params: any[]) => object;
  getMeta?: (data: K) => PagePropertiesDTO;
  getAuxiliary?: (raw: K) => A | undefined;
  params?: Record<string, any>;
  enabled?: boolean;
  pagination?: Partial<Pagination>;
  queryKey?: string;
};

export default function DataProvider<K, T, A = unknown>({
  params,
  getData,
  getQuery,
  getMeta,
  getAuxiliary,
  normalize,
  enabled = true,
  pagination,
  children,
  queryKey,
}: Props<K, T, A>) {
  const [totalSize, setTotalSize] = useState<number>(0);
  const [dataPagination, setPagination] = useState<Pagination>({
    pageSize: pagination?.pageSize ?? 25,
    pageIndex: pagination?.pageIndex ?? 0,
    page: pagination?.page ?? 0,
  });

  const queryParams = useMemo(() => ({
    ...params,
    pagination: dataPagination,
  }), [params, dataPagination]);

  const {
    data,
    error,
    isFetching,
    isLoading,
    isError,
    isFetched,
    refetch,
  } = useQuery<K, Error>({
    queryKey: compact([...values(queryParams), queryKey ?? null]),
    queryFn: ({ signal }) => getData(getQuery(queryParams), { signal }),
    enabled,
  });

  useEffect(() => {
    const meta = getMeta?.((data ?? {}) as K) ?? {};
    setTotalSize(meta?.totalSize ?? 0);
  }, [data]);

  const normalized = useMemo(() => (
    normalize?.((data ?? {}) as K) ?? data as T[]
  ), [data, queryParams]);

  const auxiliary = useMemo(
    () => (getAuxiliary ? getAuxiliary((data ?? {}) as K) : undefined),
    [data, getAuxiliary],
  );

  const value = useMemo(() => ({
    data: normalized,
    auxiliary,
    refetch,
    error,
    isLoading,
    isFetching,
    isFetched,
    isError,
    isEmpty: normalized?.length === 0 && isFetched,
    setPagination,
    pagination: dataPagination,
    totalSize,
    setTotalSize,
  }), [
    normalized,
    auxiliary,
    refetch,
    error,
    isLoading,
    isFetching,
    isFetched,
    isError,
    dataPagination,
    setPagination,
    totalSize,
    setTotalSize,
  ]);

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}
